// src/components/ResetPassword.jsx
// NEW PAGE — handles password reset after user clicks link in their email.
//
// SETUP REQUIRED (one-time):
// Firebase Console → Authentication → Templates → Password reset
//   → Customize action URL → set to:
//   https://esdras-app-frontend.vercel.app/reset-password
//
// App.jsx: Add this route inside your <Routes>:
//   <Route path="/reset-password" element={<ResetPassword />} />

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { auth } from '../firebase';
import {
  verifyPasswordResetCode,
  confirmPasswordReset,
  applyActionCode,
} from 'firebase/auth';
import { toast } from 'react-hot-toast';

const GOLD = '#B8860B';
const NAVY = '#001F3F';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate        = useNavigate();

  const oobCode = searchParams.get('oobCode');
  const mode    = searchParams.get('mode'); // 'resetPassword' | 'verifyEmail' | 'recoverEmail'

  const [step,        setStep]        = useState('loading'); // 'loading' | 'form' | 'success' | 'error'
  const [email,       setEmail]       = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPw,   setConfirmPw]   = useState('');
  const [loading,     setLoading]     = useState(false);
  const [errorMsg,    setErrorMsg]    = useState('');
  const [showPw,      setShowPw]      = useState(false);

  // ── On mount: verify the oobCode is valid ──────────────────────────────────
  useEffect(() => {
    if (!oobCode) {
      setStep('error');
      setErrorMsg('Invalid or missing reset link. Please request a new password reset from the login page.');
      return;
    }

    // Handle email verification links that also pass through here
    if (mode === 'verifyEmail') {
      applyActionCode(auth, oobCode)
        .then(() => {
          toast.success('Email verified successfully!');
          navigate('/login');
        })
        .catch(() => {
          setStep('error');
          setErrorMsg('Email verification link is invalid or has expired. Please request a new one from the login page.');
        });
      return;
    }

    // For password reset: verify the code and get the email
    verifyPasswordResetCode(auth, oobCode)
      .then((userEmail) => {
        setEmail(userEmail);
        setStep('form');
      })
      .catch(() => {
        setStep('error');
        setErrorMsg('This reset link is invalid or has expired. Password reset links expire after 1 hour. Please request a new one.');
      });
  }, [oobCode, mode, navigate]);

  // ── Submit new password ────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPw) {
      toast.error('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await confirmPasswordReset(auth, oobCode, newPassword);
      setStep('success');
      toast.success('Password reset successfully!');
    } catch (err) {
      if (err.code === 'auth/expired-action-code') {
        setStep('error');
        setErrorMsg('This reset link has expired. Please request a new one.');
      } else if (err.code === 'auth/weak-password') {
        toast.error('Password is too weak. Use at least 8 characters.');
      } else {
        toast.error(err.message || 'Failed to reset password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // ── RENDER ─────────────────────────────────────────────────────────────────
  return (
    <div style={pageStyle}>
      <style>{`@keyframes spin{100%{transform:rotate(360deg)}}`}</style>

      <div style={card}>
        <img src="/icons/esdras-logo.png" alt="Esdras" style={{ width: '70px', marginBottom: '12px' }}
          onError={e => { e.target.style.display = 'none'; }} />
        <h1 style={{ color: GOLD, letterSpacing: '3px', fontSize: '1.4rem', margin: '0 0 6px' }}>ESDRAS</h1>

        {/* LOADING */}
        {step === 'loading' && (
          <div style={{ textAlign: 'center', padding: '30px 0' }}>
            <div style={spinner} />
            <p style={{ color: '#888', fontSize: '0.82rem', marginTop: '14px', letterSpacing: '1px' }}>
              VERIFYING RESET LINK...
            </p>
          </div>
        )}

        {/* FORM */}
        {step === 'form' && (
          <>
            <div style={{ fontSize: '2.5rem', margin: '8px 0' }}>🔒</div>
            <h2 style={{ color: 'white', fontWeight: '300', margin: '0 0 6px', fontSize: '1.2rem' }}>
              Set New Password
            </h2>
            {email && (
              <p style={{ color: '#888', fontSize: '0.78rem', margin: '0 0 24px' }}>
                For account: <span style={{ color: GOLD }}>{email}</span>
              </p>
            )}

            <form onSubmit={handleSubmit} style={{ width: '100%' }}>
              <div style={{ position: 'relative', marginBottom: '12px' }}>
                <input
                  type={showPw ? 'text' : 'password'}
                  placeholder="New password (min. 8 characters)"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  required
                  style={inputStyle}
                />
              </div>

              <div style={{ position: 'relative', marginBottom: '8px' }}>
                <input
                  type={showPw ? 'text' : 'password'}
                  placeholder="Confirm new password"
                  value={confirmPw}
                  onChange={e => setConfirmPw(e.target.value)}
                  required
                  style={inputStyle}
                />
              </div>

              {/* Password strength indicators */}
              <div style={{ display: 'flex', gap: '6px', marginBottom: '16px' }}>
                {[
                  { label: '8+ chars',   ok: newPassword.length >= 8 },
                  { label: 'Uppercase',  ok: /[A-Z]/.test(newPassword) },
                  { label: 'Number',     ok: /\d/.test(newPassword) },
                ].map(c => (
                  <div key={c.label} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.65rem', color: c.ok ? '#4caf50' : '#555' }}>
                    <span>{c.ok ? '✓' : '○'}</span>
                    <span>{c.label}</span>
                  </div>
                ))}
                <button type="button" onClick={() => setShowPw(!showPw)}
                  style={{ marginLeft: 'auto', background: 'none', border: 'none', color: GOLD, fontSize: '0.7rem', cursor: 'pointer', fontFamily: 'Montserrat, sans-serif' }}>
                  {showPw ? 'HIDE' : 'SHOW'}
                </button>
              </div>

              <button type="submit" disabled={loading} style={{ ...btnStyle, opacity: loading ? 0.7 : 1 }}>
                {loading ? 'UPDATING...' : 'RESET PASSWORD'}
              </button>
            </form>
          </>
        )}

        {/* SUCCESS */}
        {step === 'success' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '3rem', margin: '8px 0 16px' }}>✅</div>
            <h2 style={{ color: '#4caf50', margin: '0 0 12px', fontWeight: '700' }}>Password Updated!</h2>
            <p style={{ color: '#aaa', fontSize: '0.85rem', lineHeight: '1.7', margin: '0 0 24px' }}>
              Your password has been successfully updated.
              You can now log in with your new password.
            </p>
            <button onClick={() => navigate('/login')} style={btnStyle}>
              GO TO LOGIN
            </button>
          </div>
        )}

        {/* ERROR */}
        {step === 'error' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '3rem', margin: '8px 0 16px' }}>⚠️</div>
            <h2 style={{ color: '#ff4d4d', margin: '0 0 12px', fontWeight: '700' }}>Link Expired</h2>
            <p style={{ color: '#aaa', fontSize: '0.85rem', lineHeight: '1.7', margin: '0 0 24px' }}>
              {errorMsg}
            </p>
            <button onClick={() => navigate('/login')} style={btnStyle}>
              BACK TO LOGIN
            </button>
          </div>
        )}

        <p style={{ color: '#444', fontSize: '0.68rem', marginTop: '24px' }}>
          Need help?{' '}
          <a href="mailto:esdrastechnologiesinc@gmail.com" style={{ color: GOLD, textDecoration: 'none' }}>
            esdrastechnologiesinc@gmail.com
          </a>
        </p>
      </div>
    </div>
  );
}

const pageStyle = { minHeight: '100vh', background: NAVY, display: 'grid', placeItems: 'center', padding: '20px', fontFamily: 'Montserrat, sans-serif' };
const card      = { background: 'rgba(255,255,255,0.03)', padding: '44px 40px', borderRadius: '30px', width: '90%', maxWidth: '420px', textAlign: 'center', border: '1px solid rgba(184,134,11,0.2)' };
const inputStyle = { width: '100%', padding: '15px', borderRadius: '12px', border: '1px solid #333', background: '#000', color: '#fff', fontSize: '0.95rem', fontFamily: 'Montserrat, sans-serif', boxSizing: 'border-box', outline: 'none' };
const btnStyle  = { width: '100%', padding: '18px', background: GOLD, color: 'black', border: 'none', borderRadius: '50px', fontWeight: '900', cursor: 'pointer', fontSize: '0.95rem', fontFamily: 'Montserrat, sans-serif', letterSpacing: '1px' };
const spinner   = { width: '36px', height: '36px', border: `3px solid rgba(184,134,11,0.15)`, borderTop: `3px solid ${GOLD}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto' };
